import React, { useState, useEffect } from 'react'
import { useQuery, useLazyQuery } from '../../../Hooks/graphql_utils'
import { gql } from '@apollo/client'
import { uniqBy } from 'lodash'
import {
    Map,
    Row,
    Col,
    H3,
    FormSimpleSelect,
    CardWithBreadcrumb,
    Card,
    Divider,
    Spinner,
    Button,
    ErrorMessage
} from '../../../Elements'
import { CAN_EDIT_GUNITE_SCHEDULE, CAN_EDIT_PLASTER_SCHEDULE } from '../../../utils/enums'
import JobFilters from '../JobFilters'
import {
    PebblePrepColor,
    PebbleAppColor,
    PebbleDetailColor,
    GuniteColor,
    WarrantyColor,
    PresiteColor
} from '../../../Elements/Dnd/shared'
import { List, AutoSizer } from 'react-virtualized'
import { NonIdealState } from '@blueprintjs/core'
import { useMediaQuery } from 'react-responsive'
import { useHasRight } from '../../../Hooks/hasRight'
import { Icon, Popover, PopoverInteractionKind, Position } from '@blueprintjs/core'
import { Legend } from '../../../Elements/Map/colors'
import { AddressLinkText } from "../../../Elements/Address";
import { CanViewJob } from "../../Auth";

const LAZY_QUERY = gql`
    query jobMapLazyQuery ($searchCriteria: JobSearchCriteriaInput) {
        map_jobs(searchCriteria: $searchCriteria) {
            id
            job_id
            job_detail_id
            name
            company
            address_line_1
            city
            key_map
            lat
            lon
            temp_lat
            temp_lon
            color {
                id
                name
                material_type{
                    id
                    name
                }
            }
            plan_sa
            is_scheduled_app_date_search
            category{
                id
                name
            }
            gunite_date
            gunite_crew{
                id
                name
            }
            gunite_type{
                id
                name
            }
            pebble_app_date
            pebble_phase_type{
                id
                name
            }
            pebble_crew{
                id
                name
            }
            presite_scheduled_date
            presite_supervisor{
                id
                name
            }
            crew_repair_crew {
                id
                name
            }
            crew_repair_date
        }
        customers: lookupCustomer {
            id
            company_name
            is_active
        }
        customer_contacts {
            id
            first_name
            last_name
        }
        lookupJobCategory {
            id
            name
        }
        lookupPebbleColor {
            id
            name
        }
        lookupJobStatus {
            id
            name
        }
        lookupCity {
            id
            name
        }
        lookupQuickFocusMap {
            id
            name
            latitude
            longitude
        }
    }
`
const QUERY = gql`
    query  {
        customers: lookupCustomer {
            id
            company_name
            is_active
        }
        homeowners {
            id
            first_name
            last_name
        }
        customer_contacts {
            id
            first_name
            last_name
        }
        lookupJobCategory {
            id
            name
        }
        lookupPebbleColor {
            id
            name
        }
        lookupJobStatus {
            id
            name
        }
        lookupCity {
            id
            name
        }
        lookupQuickFocusMap {
            id
            name
            latitude
            longitude
        }
        lookupJobType {
            id
            name
        }
    }
`

const jobNameStyle = {
    color: '#106ba3',
    fontSize: 'large'
}

const unverifiedStyle = {
    color: 'red',
    fontWeight: 'bold',
    marginTop: 2,

}
const unmappedJobStyle = {
    verticalAlign: 'middle'
}
const unmappedApproximateStyle = {
    verticalAlign: 'middle',
    color: 'red'
}


const DEFAULT_PEBBLE = '#9d5c10'
const DEFAULT_MARKER = 'black'
const GUNITE_FLASH = '#ff1493'

const LinkToJob = ({ jobId }) => (
    <CanViewJob>
        <Button minimal icon='share' onClick={() => window.open(`/job/${jobId}/edit`, "_blank")} />
    </CanViewJob>
)
const LinkToJobDetail = ({ jobId, jobDetailId }) => (
    <CanViewJob>
        <Button minimal icon='share' onClick={() => window.open(`/job/${jobId}/edit/${jobDetailId}`, "_blank")} />
    </CanViewJob>
)

const MapMarker = ({ details, isVerifiedAddress }) => <>
    <div style={jobNameStyle}>{details.name}<LinkToJobDetail jobId={details.job_id} jobDetailId={details.job_detail_id} /></div>
    <div>{details.company}</div>
    <div>
        <AddressLinkText
            line1={details.address_line_1}
            line2={details.address_line_2}
            city={details.city}
            state={details.state}
            zip={details.zip_code}
        />
    </div>
    {details.key_map && <div>Key Map: {details.key_map}</div>}
    {details.color && details.color.material_type && <div>{`${details.color.material_type.name} - ${details.color.name}`}</div>}
    {details.plan_sa && <div>SA: {details.plan_sa}</div>}
    {details.category?.id === 3 && details.crew_repair_date !== null && <div><em>Crew Repair</em></div>}
    {!isVerifiedAddress && <div style={unverifiedStyle}>Address Unverified: Approximate Location</div>}
</>

const UnmappableJob = ({ details }) => <p>
    <LinkToJob jobId={details.job_id} />
    <strong style={details.temp_lat && details.temp_lon ? unmappedApproximateStyle : unmappedJobStyle}>{details.name}</strong>
</p>

const GetMarkerColor = (details) => {
    return details.category?.id === 3 ? //Warranty
        WarrantyColor :
        !details.is_scheduled_app_date_search && details.category?.id === 1
            && details.presite_scheduled_date && details.presite_scheduled_date < details.gunite_date ? //Gunite Presite
            PresiteColor :
            details.category?.id === 1 && details.gunite_type?.id === 9 ? //Gunite Flash
                GUNITE_FLASH :
                details.category?.id === 1 ? //Gunite
                    GuniteColor :
                    details.is_scheduled_app_date_search && details.category?.id === 2 ?
                        PebbleAppColor :
                        details.category?.id === 2 && details.presite_scheduled_date && details.presite_scheduled_date < details.pebble_app_date ? //Pebble Presite
                            PresiteColor :
                            details.category?.id === 2 && details.pebble_phase_type?.id === 1 ? //Pebble Prep
                                PebblePrepColor :
                                details.category?.id === 2 && details.pebble_phase_type?.id === 2 ? //Pebble App
                                    PebbleAppColor :
                                    details.category?.id === 2 && details.pebble_phase_type?.id === 3 ? //Pebble Detail
                                        PebbleDetailColor :
                                        details.category?.id === 2 ? //Default Pebble
                                            DEFAULT_PEBBLE
                                            : DEFAULT_MARKER

}
export default () => {
    const [mapCenter, setMapCenter] = useState(1)
    const [hasFetched, setHasFetched] = useState(false)
    const [isLoading, setIsLoading] = useState(true)

    const isStackedMap = useMediaQuery({ query: '(max-width: 992px)' })

    const canViewUnmappedJobs = useHasRight(CAN_EDIT_GUNITE_SCHEDULE) || useHasRight(CAN_EDIT_PLASTER_SCHEDULE)

    const [fetchItems, { loading: refetchLoading, data: refetchData,
        networkStatus: refetchNetworkStatus, error: refetchError }] = useLazyQuery(LAZY_QUERY, {

            notifyOnNetworkStatusChange: true,
            onCompleted: () => {
                if (!hasFetched) {
                    setHasFetched(true)
                }
            }
        })

    const { error, data } = useQuery(QUERY, {
        notifyOnNetworkStatusChange: true
    })

    useEffect(() => {
        if (data && isLoading) {
            setIsLoading(false)
        }
    }, [data])

    if (error || refetchError) {
        return <ErrorMessage />
    }

    const isRefetchLoadingOrRefreshing = refetchLoading || refetchNetworkStatus === 2

    const customer_contacts = ((refetchData && refetchData.customer_contacts) || (data && data.customer_contacts)) || []
    const homeowners = (refetchData?.homeowners || data?.homeowners) || []
    const positions = ((refetchData && refetchData?.lookupQuickFocusMap) || (data && data?.lookupQuickFocusMap)) || []

    const mappedCustomerContacts = customer_contacts.map(s => ({ ...s, name: `${s.first_name} ${s.last_name}` })) || []
    const mappedHomeowners = homeowners.map(h => ({ ...h, name: `${h.first_name} ${h.last_name}` })) || []
    const mappedPositions = positions.map(p => ({ id: p.id, name: p.name, position: [p.latitude, p.longitude] })) || []

    const mappedJobs = ((refetchData && refetchData.map_jobs) || []).filter(j => (j.lat !== null && j.lon !== null) || (j.temp_lat !== null && j.temp_lon !== null))
        .map(d => {
            const isVerifiedAddress = d.lat !== null && d.lon !== null
            return (
                {
                    position: isVerifiedAddress ? [d.lat, d.lon] : [d.temp_lat, d.temp_lon],
                    markerColor: GetMarkerColor(d).toLowerCase(),
                    children: <MapMarker key={`marker_${d.job_id}`} details={d} isVerifiedAddress={isVerifiedAddress} />
                })
        })


    const unmappedJobs = uniqBy(((refetchData && refetchData.map_jobs) || []).filter(j => j.lat === null || j.lon === null), 'job_id')
    const center = mappedPositions.find(p => p.id === mapCenter)

    const WrappedUnmappableJob = ({ key, index, style }) => <div key={key} style={style}><UnmappableJob details={unmappedJobs[index]} /></div>

    const ColForJobsWithoutLatAndLong = () =>
        <Col lg={3.99}>
            <H3>Jobs Without Verified Addresses</H3>
            <div>*<span style={{ color: 'red', fontWeight: 'bold' }}>Job</span> approximate location shown on the map </div>
            <Divider />
            {!hasFetched
                ? <NonIdealState
                    icon='geosearch'
                    title='Enter search criteria'
                />
                : isRefetchLoadingOrRefreshing
                    ? <Spinner />
                    : !unmappedJobs || !unmappedJobs.length || unmappedJobs.length < 1
                        ? <NonIdealState
                            icon='zoom-out'
                            title='No jobs without verified addresses for current criteria'
                        />
                        : <>
                            <div style={{ height: '600px' }}>
                                <AutoSizer>
                                    {({ width }) => {
                                        return <List
                                            height={600}
                                            rowCount={unmappedJobs.length}
                                            rowRenderer={WrappedUnmappableJob}
                                            width={width}
                                            rowHeight={30}
                                        />
                                    }}
                                </AutoSizer>
                            </div>
                        </>
            }
        </Col>

    const markerColors = [
        { color: DEFAULT_PEBBLE, name: 'Pebble' },
        { color: PebbleAppColor, name: 'App' },
        { color: PebblePrepColor, name: 'Prep' },
        { color: PebbleDetailColor, name: 'Detail' },
        { color: GUNITE_FLASH, name: 'Flash' },
        { color: GuniteColor, name: 'Gunite' },
        { color: WarrantyColor, name: 'Warranty' },
        { color: PresiteColor, name: 'Presite' },
        { color: DEFAULT_MARKER, name: 'Uncategorized' }]

    const LegendPopver = () => {
        const [isOpen, setIsOpen] = useState(false)
        return <Popover
            interactionKind={PopoverInteractionKind.CLICK}
            position={Position.TOP}
            content={<Legend markerColors={markerColors} />}
            isOpen={isOpen}
            onInteraction={(state) => setIsOpen(state)}
            children={
                <Icon icon='map-marker' intent='primary' style={{ display: 'inline-block', verticalAlign: 'middle' }} iconSize={25} />
            }
        />
    }


    return (isLoading
        ? <></>
        : <>
            <CardWithBreadcrumb sticky={false} items={[{ text: 'Job Search Map', icon: 'globe' }]} />
            <JobFilters
                refetch={fetchItems}
                supervisors={mappedCustomerContacts}
                homeowners={mappedHomeowners}
                customers={data && data.customers}
                jobStatuses={data && data.lookupJobStatus}
                pebbleColors={data && data.lookupPebbleColor}
                jobCategories={data && data.lookupJobCategory}
                cities={data && data.lookupCity}
                jobTypes = {data && data.lookupJobType}
                isLoadingOrRefreshing={isRefetchLoadingOrRefreshing}
                forMap={true}
            />
            <Row>
                <Col lg={12}>
                    <Card>
                        <Row>
                            {!isStackedMap && canViewUnmappedJobs && <>
                                <ColForJobsWithoutLatAndLong />
                                <Divider />
                            </>
                            }
                            <Col lg={canViewUnmappedJobs ? 8 : 12}>
                                <Row style={{ alignItems: 'center' }}>
                                    <Col xs={11}>
                                        <FormSimpleSelect
                                            label='Quick Focus:'
                                            items={mappedPositions}
                                            value={mappedPositions.find(p => p.id === mapCenter)}
                                            disabled={isRefetchLoadingOrRefreshing}
                                            onChange={(evt) => setMapCenter(evt.id)}
                                        />
                                    </Col>
                                    <Col xs={1} style={{ margin: 'auto', textAlign: 'center' }}>
                                        <LegendPopver />
                                    </Col>
                                </Row>
                                <Map
                                    center={center?.position}
                                    height="600px"
                                    width="100%"
                                    markers={mappedJobs}
                                    isLoading={isRefetchLoadingOrRefreshing}
                                />
                            </Col>
                        </Row>
                    </Card>
                    {isStackedMap && canViewUnmappedJobs &&
                        <Card style={{ minHeight: 300 }}>
                            <Row>
                                <ColForJobsWithoutLatAndLong />
                                <Divider />
                            </Row>
                        </Card>
                    }
                </Col>
            </Row>
        </>)
}